// ---------------------------------------------------------------------------
// ThemeFixer.js — Global runtime theme override
// ---------------------------------------------------------------------------
// Watches the DOM for dark inline backgrounds and replaces them with
// Warm Stone equivalents. Drop into App.js as <ThemeFixer /> and every
// page (even ones not yet rewritten) will render in the light theme.
// ---------------------------------------------------------------------------
import { useEffect } from "react";

// ── Color mappings: dark → light ──────────────────────────────────────────
const BG_MAP = {
  "#0a0f1a": "#F5F0EB",
  "#0b1120": "#F5F0EB",
  "#0d1320": "#F5F0EB",
  "#0d1321": "#F5F0EB",
  "#0f1724": "#F5F0EB",
  "#111827": "#FFFFFF",
  "#131d30": "#F5F0EB",
  "#1a1a2e": "#F5F0EB",
  "#1a2332": "#FFFFFF",
  "#1e293b": "#FFFFFF",
  "#1e2a3a": "#FFFFFF",
  "#1f2937": "#F8F5F0",
  "#2a3a4e": "#F8F5F0",
};

const TEXT_MAP = {
  "#e0e0e0": "#3C2A14",
  "#e2e8f0": "#3C2A14",
  "#e5e5e5": "#3C2A14",
  "#f0f0f0": "#3C2A14",
  "#f1f5f9": "#3C2A14",
  "#f8fafc": "#3C2A14",
  "#cbd5e1": "#6B5B47",
  "#94a3b8": "#8B7355",
  "#9ca3af": "#8B7355",
  "#a0aec0": "#8B7355",
  "#64748b": "#8B7355",
  "#6b7280": "#8B7355",
  "#d1d5db": "#6B5B47",
};

const BORDER_MAP = {
  "#2a3a4e": "#E5DDD3",
  "#1e293b": "#E5DDD3",
  "#334155": "#E5DDD3",
  "#374151": "#E5DDD3",
  "#1f2937": "#E5DDD3",
};

// ── Skip sidebar elements ─────────────────────────────────────────────────
function isSidebar(el) {
  let n = el;
  for (let i = 0; i < 10; i++) {
    if (!n) break;
    const s = n.style;
    if (s) {
      const bg = (s.background || s.backgroundColor || "").toLowerCase();
      if (bg.includes("44, 40, 37") || bg.includes("2c2825")) return true;
      if (s.position === "fixed" && s.width === "200px") return true;
    }
    if (n.tagName === "NAV") return true;
    n = n.parentElement;
  }
  return false;
}

function norm(c) {
  return (c || "").toLowerCase().trim();
}

// Build a combined lookup of hex values (both # and rgb forms)
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${r}, ${g}, ${b})`;
}

// Build extended maps that include rgb equivalents
const BG_ALL = {};
for (const [k, v] of Object.entries(BG_MAP)) {
  BG_ALL[k] = v;
  BG_ALL[hexToRgb(k)] = v;
}

const TEXT_ALL = {};
for (const [k, v] of Object.entries(TEXT_MAP)) {
  TEXT_ALL[k] = v;
  TEXT_ALL[hexToRgb(k)] = v;
}

const BORDER_ALL = {};
for (const [k, v] of Object.entries(BORDER_MAP)) {
  BORDER_ALL[k] = v;
  BORDER_ALL[hexToRgb(k)] = v;
}

function fixEl(el) {
  if (!el || !el.style || isSidebar(el)) return;
  const s = el.style;

  // Fix background
  const bg = norm(s.background || s.backgroundColor);
  if (bg && BG_ALL[bg]) {
    s.background = BG_ALL[bg];
    s.backgroundColor = BG_ALL[bg];
  }

  // Fix text color (only neutral gray/white text, not accent colors)
  const color = norm(s.color);
  if (color && TEXT_ALL[color]) {
    s.color = TEXT_ALL[color];
  }

  // Fix borders
  const bc = norm(s.borderColor);
  if (bc && BORDER_ALL[bc]) s.borderColor = BORDER_ALL[bc];

  // Fix border shorthand (check if it contains a dark color)
  for (const prop of ["border", "borderBottom", "borderTop", "borderLeft", "borderRight"]) {
    const val = norm(s[prop]);
    if (val) {
      for (const [dk, lt] of Object.entries(BORDER_ALL)) {
        if (val.includes(dk)) {
          s[prop] = val.replace(dk, lt);
          break;
        }
      }
    }
  }
}

function fixInputs() {
  document.querySelectorAll("input, select, textarea").forEach((el) => {
    if (isSidebar(el)) return;
    const cs = getComputedStyle(el);
    const bgStr = cs.backgroundColor;
    const m = bgStr.match(/\d+/g);
    if (m) {
      const r = parseInt(m[0]);
      const g = parseInt(m[1]);
      const b = parseInt(m[2]);
      if (r < 60 && g < 60 && b < 80) {
        el.style.backgroundColor = "#FFFFFF";
        el.style.color = "#3C2A14";
        el.style.borderColor = "#E5DDD3";
      }
    }
  });
}

function fixAll() {
  document.querySelectorAll("[style]").forEach(fixEl);
  fixInputs();
  // Also fix th/td with inherited dark backgrounds
  document.querySelectorAll("th, td, tr").forEach((el) => {
    if (isSidebar(el)) return;
    const cs = getComputedStyle(el);
    const m = cs.backgroundColor.match(/\d+/g);
    if (m) {
      const r = parseInt(m[0]);
      const g = parseInt(m[1]);
      const b = parseInt(m[2]);
      if (r < 60 && g < 60 && b < 80) {
        el.style.backgroundColor = r < 25 ? "#F8F5F0" : "#FFFFFF";
        el.style.color = "#3C2A14";
      }
    }
  });
}

export default function ThemeFixer() {
  useEffect(() => {
    // Initial fix after a tiny delay for React to finish rendering
    const t1 = setTimeout(fixAll, 50);
    const t2 = setTimeout(fixAll, 200);
    const t3 = setTimeout(fixAll, 500);

    // MutationObserver for dynamic changes
    const obs = new MutationObserver((muts) => {
      for (const mut of muts) {
        if (mut.type === "childList") {
          mut.addedNodes.forEach((n) => {
            if (n.nodeType === 1) {
              fixEl(n);
              n.querySelectorAll?.("[style]")?.forEach(fixEl);
            }
          });
        }
        if (mut.type === "attributes" && mut.attributeName === "style") {
          fixEl(mut.target);
        }
      }
    });

    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    // Periodic cleanup catches React re-renders
    const interval = setInterval(fixAll, 600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      obs.disconnect();
      clearInterval(interval);
    };
  }, []);

  return null;
}
